/**
 * Typed wrappers for the Attendance module (migration 056). Paths + shapes
 * are taken VERBATIM from backend/internal/router/router.go and
 * handlers/attendance.go.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by the
 * shared axios instance (lib/api/client.ts). Every /attendance route is
 * fenced server-side by RequireFeature("time_tracking"); the api client turns
 * the backend's 403 { error: "upgrade_required" } into a /pricing redirect.
 *
 * No client-supplied tenant_id / employee_id is ever sent for self-scoped
 * routes: the server derives both from the session. The active legal entity
 * (G18) travels via the shared X-Entity-Id header the client sets.
 */

import { api, blobErrorToApi } from "./client";
import type {
  AttendanceSettings,
  BatchPunchResultItem,
  CreateLocationRequest,
  CreateRegularizationRequest,
  DecisionRequest,
  ListDaysResponse,
  ListLocationsResponse,
  ListRegularizationsResponse,
  OfficeLocation,
  PunchExemptPerson,
  PunchRequest,
  Regularization,
  PunchResponse,
  TodayResponse,
  UpdateAttendanceSettingsRequest,
  UpdateLocationRequest,
} from "@/types/attendance";

export const ATTENDANCE_BASE = "/api/v1/attendance";

type DateRangeQuery = { from?: string; to?: string; date?: string };

// POST /attendance/punch — single punch (self). Idempotent on uuid.
export async function punch(body: PunchRequest): Promise<PunchResponse> {
  const { data } = await api.post<PunchResponse>(`${ATTENDANCE_BASE}/punch`, body);
  return data;
}

// POST /attendance/punch/batch — offline-queue flush. Per-item result.
export async function punchBatch(
  punches: PunchRequest[],
): Promise<{ results: BatchPunchResultItem[] }> {
  const { data } = await api.post<{ results: BatchPunchResultItem[] }>(
    `${ATTENDANCE_BASE}/punch/batch`,
    { punches },
  );
  return data;
}

// GET /attendance/today — open punch state + today's derived status.
export async function getToday(signal?: AbortSignal): Promise<TodayResponse> {
  const { data } = await api.get<TodayResponse>(`${ATTENDANCE_BASE}/today`, {
    signal,
  });
  return data;
}

// GET /attendance/me?from=&to= — own day rows.
export async function listMyDays(
  params?: DateRangeQuery,
  signal?: AbortSignal,
): Promise<ListDaysResponse> {
  const { data } = await api.get<ListDaysResponse>(`${ATTENDANCE_BASE}/me`, {
    params,
    signal,
  });
  return data;
}

// GET /attendance/team?date= OR ?from=&to= — reports/entity day rows.
// needs_review=1 (narrowing-only) keeps the review queue: flagged days HR
// has not marked reviewed yet.
// roster=1: everyone in the caller's scope (a manager's reports, HR's whole
// company) gets a line per date — including people who did not punch
// (no_record=true) — not only those with an attendance record.
export async function listTeamDays(
  params?: DateRangeQuery & { employee_id?: number; needs_review?: 1; roster?: 1 },
  signal?: AbortSignal,
): Promise<ListDaysResponse> {
  const { data } = await api.get<ListDaysResponse>(`${ATTENDANCE_BASE}/team`, {
    // overlays=1: someone on approved leave / a holiday / a weekly off
    // with no attendance record yet comes back as a synthetic day row
    // instead of "No punch" (the server skips them for the review queue).
    params: { ...params, overlays: 1 },
    signal,
  });
  return data;
}

// GET /attendance/days?employee_id=&from=&to= — admin drill-down.
export async function listDays(
  params?: DateRangeQuery & { employee_id?: number },
  signal?: AbortSignal,
): Promise<ListDaysResponse> {
  const { data } = await api.get<ListDaysResponse>(`${ATTENDANCE_BASE}/days`, {
    params,
    signal,
  });
  return data;
}

// --- regularizations ---

export async function createRegularization(
  body: CreateRegularizationRequest,
): Promise<Regularization> {
  const { data } = await api.post<Regularization>(
    `${ATTENDANCE_BASE}/regularizations`,
    body,
  );
  return data;
}

// `exclude_self=1` narrows an approver's list to rows they can ACT on
// (own requests removed).
export async function listRegularizations(
  params?: { status?: string; scope?: "self"; exclude_self?: boolean },
  signal?: AbortSignal,
): Promise<ListRegularizationsResponse> {
  const { exclude_self, ...rest } = params ?? {};
  const { data } = await api.get<ListRegularizationsResponse>(
    `${ATTENDANCE_BASE}/regularizations`,
    {
      params: { ...rest, ...(exclude_self ? { exclude_self: 1 } : {}) },
      signal,
    },
  );
  return data;
}

export async function approveRegularization(
  id: number,
  body?: DecisionRequest,
): Promise<Regularization> {
  const { data } = await api.post<Regularization>(
    `${ATTENDANCE_BASE}/regularizations/${id}/approve`,
    body ?? {},
  );
  return data;
}

export async function rejectRegularization(
  id: number,
  body: DecisionRequest,
): Promise<Regularization> {
  const { data } = await api.post<Regularization>(
    `${ATTENDANCE_BASE}/regularizations/${id}/reject`,
    body,
  );
  return data;
}

export async function cancelRegularization(
  id: number,
): Promise<Regularization> {
  const { data } = await api.post<Regularization>(
    `${ATTENDANCE_BASE}/regularizations/${id}/cancel`,
    {},
  );
  return data;
}

// --- office locations (geofence anchors) ---

export async function listLocations(
  signal?: AbortSignal,
): Promise<ListLocationsResponse> {
  const { data } = await api.get<ListLocationsResponse>(
    `${ATTENDANCE_BASE}/locations`,
    { signal },
  );
  return data;
}

export async function createLocation(
  body: CreateLocationRequest,
): Promise<OfficeLocation> {
  const { data } = await api.post<OfficeLocation>(
    `${ATTENDANCE_BASE}/locations`,
    body,
  );
  return data;
}

export async function updateLocation(
  id: number,
  body: UpdateLocationRequest,
): Promise<OfficeLocation> {
  const { data } = await api.patch<OfficeLocation>(
    `${ATTENDANCE_BASE}/locations/${id}`,
    body,
  );
  return data;
}

export async function deleteLocation(id: number): Promise<void> {
  await api.delete(`${ATTENDANCE_BASE}/locations/${id}`);
}

// --- settings + recompute ---

export async function getSettings(
  signal?: AbortSignal,
): Promise<AttendanceSettings> {
  const { data } = await api.get<AttendanceSettings>(
    `${ATTENDANCE_BASE}/settings`,
    { signal },
  );
  return data;
}

export async function updateSettings(
  body: UpdateAttendanceSettingsRequest,
): Promise<AttendanceSettings> {
  const { data } = await api.patch<AttendanceSettings>(
    `${ATTENDANCE_BASE}/settings`,
    body,
  );
  return data;
}

// --- "Doesn't need to punch" (migration 167; HR admins) ---

export async function listPunchExempt(
  signal?: AbortSignal,
): Promise<{ people: PunchExemptPerson[] }> {
  const { data } = await api.get<{ people: PunchExemptPerson[] }>(
    `${ATTENDANCE_BASE}/punching/exempt`,
    { signal },
  );
  return data;
}

/** Set / clear for one person. 403 own_record for oneself. */
export async function setPunchExempt(
  userId: number,
  exempt: boolean,
): Promise<{ user_id: number; exempt: boolean; changed: boolean }> {
  const { data } = await api.put<{ user_id: number; exempt: boolean; changed: boolean }>(
    `${ATTENDANCE_BASE}/punching/exempt/${userId}`,
    { exempt },
  );
  return data;
}

// GET /attendance/export?month=YYYY-MM — monthly CSV (HR: everyone, manager:
// their reports). Returns the Blob; the caller picks the filename.
export async function exportMonth(month: string): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${ATTENDANCE_BASE}/export`, {
      // overlays=1: also a line for each holiday / weekly off / approved
      // leave / WFH day without an attendance record (up to today).
      params: { month, overlays: 1 },
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// GET /attendance/monthly-summary?month=YYYY-MM — HR only. Per employee:
// present / paid leave / LOP / WFH / holidays / weekly offs, computed by
// the payroll run's own input code so the LOP matches the payroll run.
export interface AttendanceMonthlySummaryRow {
  employee_id: number;
  employee_code: string;
  name: string;
  department: string;
  employed_days: number;
  present_days: number;
  paid_leave_days: number;
  unpaid_leave_days: number;
  /** Absences counted as LOP (only when attendance drives LOP). */
  absent_lop_days: number;
  /** unpaid_leave_days + absent_lop_days — the payroll run's LOP. */
  lop_days: number;
  wfh_days: number;
  holidays: number;
  weekly_offs: number;
  overtime_minutes: number;
}

export interface AttendanceMonthlySummary {
  year: number;
  month: number;
  days_in_month: number;
  attendance_drives_lop: boolean;
  rows: AttendanceMonthlySummaryRow[];
}

export async function getMonthlySummary(
  month: string,
  signal?: AbortSignal,
): Promise<AttendanceMonthlySummary> {
  const { data } = await api.get<AttendanceMonthlySummary>(
    `${ATTENDANCE_BASE}/monthly-summary`,
    { params: { month }, signal },
  );
  return data;
}

export async function exportMonthlySummary(month: string): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${ATTENDANCE_BASE}/monthly-summary`, {
      params: { month, format: "csv" },
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// POST /attendance/days/{id}/review — HR marks one flagged day reviewed.
export async function markDayReviewed(
  id: number,
  note?: string,
): Promise<{ reviewed: number; rederive_failed: number }> {
  const { data } = await api.post<{ reviewed: number; rederive_failed: number }>(
    `${ATTENDANCE_BASE}/days/${id}/review`,
    note ? { note } : {},
  );
  return data;
}

// POST /attendance/days/{id}/unreview — HR withdraws the review of one day:
// it goes back to the review queue (an undrawn comp-off it earned through
// the review is revoked). 409 not_reviewed / comp_off_in_use.
export async function unmarkDayReviewed(
  id: number,
): Promise<{ unreviewed: number; comp_off_revoked: boolean; rederive_failed: number }> {
  const { data } = await api.post<{
    unreviewed: number;
    comp_off_revoked: boolean;
    rederive_failed: number;
  }>(`${ATTENDANCE_BASE}/days/${id}/unreview`, {});
  return data;
}

// POST /attendance/days/review — HR bulk-marks flagged days in [from, to].
export async function markRangeReviewed(body: {
  from: string;
  to: string;
  employee_id?: number;
  note?: string;
}): Promise<{ reviewed: number; rederive_failed: number }> {
  const { data } = await api.post<{ reviewed: number; rederive_failed: number }>(
    `${ATTENDANCE_BASE}/days/review`,
    body,
  );
  return data;
}

export async function recompute(body: {
  from: string;
  to: string;
  employee_id?: number;
}): Promise<{ recomputed: number; employees: number }> {
  const { data } = await api.post<{ recomputed: number; employees: number }>(
    `${ATTENDANCE_BASE}/recompute`,
    body,
  );
  return data;
}

// GET /attendance/me?from=&to=&overlays=1 — own day rows PLUS a synthetic
// row for each holiday / weekly off / approved-leave / WFH day that has no
// attendance record yet (My Time's day strip), so those days read as what
// they are instead of "No record".
export async function listMyCalendarDays(
  params: DateRangeQuery,
  signal?: AbortSignal,
): Promise<ListDaysResponse> {
  const { data } = await api.get<ListDaysResponse>(`${ATTENDANCE_BASE}/me`, {
    params: { ...params, overlays: 1 },
    signal,
  });
  return data;
}
